import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { IFluidLessonPlan, IFluidLessonPlanVariant } from '@taleemabad/db';
import {
  TextEditor,
  Typography,
  Button,
  LoadingScreen,
  AlertDialog,
  Spacing,
  TbLpStepsSwitcher,
  StyledDiv,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import {
  Direction,
  COACHING_RECORDING_CONFIG,
  COACHING_MESSAGES,
  AudioRecorderStatus,
  getExitDialogContent,
} from '@taleemabad/shared';
import { LpRecordingInviteCard } from '../shared/lp-recording-invite-card';
import { LpRecordingIdleStickyFooter } from '../shared/lp-recording-idle-sticky-footer';
import { LpRecordingStickyFooter } from '../shared/lp-recording-sticky-footer';
import { LpRecordingToast } from '../shared/lp-recording-toast';
import { RecordingActionDialogs } from '../shared/recording-action-dialogs';

const isContentEmpty = (content: any): boolean => {
  if (!content) return true;
  if (
    typeof content === 'object' &&
    !Array.isArray(content) &&
    Object.keys(content).length === 0
  ) {
    return true;
  }
  if (content?.type === 'doc' && Array.isArray(content.content)) {
    if (
      content.content.length === 1 &&
      content.content[0]?.type === 'paragraph' &&
      (!content.content[0].content || content.content[0].content.length === 0)
    ) {
      return true;
    }
  }
  if (
    Array.isArray(content) &&
    content.length === 1 &&
    content[0]?.type === 'paragraph' &&
    (!content[0].content || content[0].content.length === 0)
  ) {
    return true;
  }
  return false;
};

interface FluidLessonPlanViewerProps {
  fluidLpDetail: IFluidLessonPlan;
  isLoading?: boolean;
  showNoInternetPopup?: boolean;
  onLessonComplete: () => void;
  setShowNoInternetPopup: (value: boolean) => void;
  onSectionChange?: (
    section: string,
    sectionId?: number,
    variantId?: number,
  ) => void;
  // Recording Props
  isRecordingEnabled?: boolean;
  recordingState?: AudioRecorderStatus;
  elapsedLabel?: string;
  elapsedTime?: number;
  onRecordStart?: () => void;
  onRestart?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  showExitConfirmation?: boolean;
  onExitConfirm?: () => void;
  onExitCancel?: () => void;
  showToast?: boolean;
  onCloseToast?: () => void;
}

interface SwitcherContainerProps {
  isScrolling: boolean;
}

interface ButtonContainerProps {
  isLpCompleted: boolean;
  currentSectionIndex: number;
}

export const FluidLessonPlanViewer: React.FC<FluidLessonPlanViewerProps> = ({
  fluidLpDetail,
  isLoading = false,
  showNoInternetPopup = false,
  onLessonComplete,
  setShowNoInternetPopup,
  onSectionChange,
  isRecordingEnabled = false,
  recordingState = 'idle',
  elapsedLabel = '00:00.0',
  elapsedTime = 0,
  onRecordStart,
  onRestart,
  onPause,
  onResume,
  showExitConfirmation = false,
  onExitConfirm,
  onExitCancel,
  showToast = false,
  onCloseToast,
}) => {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [showSubmitPopup, setShowSubmitPopup] = useState(false);
  const [showRestartConfirmation, setShowRestartConfirmation] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToastInternal, setShowToastInternal] = useState(false);

  const sectionsWithContent =
    fluidLpDetail.sections?.filter((section) => {
      const sectionType = section.sectionType?.toLowerCase();
      if (sectionType === 'summary' || sectionType?.includes('resource')) {
        return false;
      }
      const validVariants = section.variants?.filter((variantWrapper) => {
        const content = variantWrapper.variant?.content;
        return content && !isContentEmpty(content);
      });
      return validVariants && validVariants.length > 0;
    }) ?? [];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (showToast) {
      setToastMessage('Recording has started');
      setShowToastInternal(true);
    }
  }, [showToast]);

  const handleNextSection = () => {
    setCurrentSectionIndex((prevIndex) => {
      const newIndex = Math.min(prevIndex + 1, sectionsWithContent.length - 1);
      scrollToTop();
      return newIndex;
    });
  };

  const handlePreviousSection = () => {
    setCurrentSectionIndex((prevIndex) => {
      const newIndex = Math.max(prevIndex - 1, 0);
      scrollToTop();
      return newIndex;
    });
  };

  const handleChangeSection = (direction: Direction) => {
    if (direction === Direction.Next) {
      handleNextSection();
      const nextSection = sectionsWithContent[currentSectionIndex + 1];
      if (nextSection?.sectionType) {
        const variant = nextSection.variants?.[0]?.variant;
        onSectionChange?.(nextSection.sectionType, nextSection.id, variant?.id);
      }
    } else if (direction === Direction.Prev) {
      handlePreviousSection();
      const prevSection = sectionsWithContent[currentSectionIndex - 1];
      if (prevSection?.sectionType) {
        const variant = prevSection.variants?.[0]?.variant;
        onSectionChange?.(prevSection.sectionType, prevSection.id, variant?.id);
      }
    }
  };

  const handleConfirmSubmit = () => {
    setShowSubmitPopup(false);
    onLessonComplete();
  };

  const handleLessonCompleteClick = () => {
    if (recordingState !== 'idle') {
      if (elapsedTime < COACHING_RECORDING_CONFIG.MIN_DURATION_MS) {
        onLessonComplete();
      } else {
        setShowSubmitPopup(true);
      }
    } else {
      onLessonComplete();
    }
  };

  const handleButtonClick = () => {
    const isLastSection =
      currentSectionIndex === sectionsWithContent.length - 1;

    if (isLastSection) {
      handleLessonCompleteClick();
    } else {
      handleChangeSection(Direction.Next);
    }
  };

  const renderVariants = (variants: IFluidLessonPlanVariant[]) => {
    return variants
      .filter((variantWrapper) => {
        const content = variantWrapper.variant?.content;
        return content && !isContentEmpty(content);
      })
      .map((variantWrapper) => {
        const { variant } = variantWrapper;
        return (
          <Spacing key={variant.id} marginBottom="md">
            <TextEditor
              content={variant.content}
              isRtl={variant.isRtl ?? false}
            />
          </Spacing>
        );
      });
  };

  if (!sectionsWithContent.length) {
    return (
      <Typography variant="textBodyLarge">
        No lesson plan sections with content available
      </Typography>
    );
  }

  const currentSection = sectionsWithContent[currentSectionIndex];
  const isLastSection = currentSectionIndex === sectionsWithContent.length - 1;

  return (
    <StyledDiv flexDirection="column">
      {isLoading && <LoadingScreen />}
      <RecordingActionDialogs
        showSubmitPopup={showSubmitPopup}
        onCloseSubmitPopup={() => setShowSubmitPopup(false)}
        onConfirmSubmit={handleConfirmSubmit}
        showRestartPopup={showRestartConfirmation}
        onCloseRestartPopup={() => setShowRestartConfirmation(false)}
        onConfirmRestart={() => {
          onRestart?.();
          setShowRestartConfirmation(false);
        }}
        showExitPopup={showExitConfirmation}
        onCloseExitPopup={() => onExitCancel?.()}
        onConfirmExit={() => onExitConfirm?.()}
        exitTitle={getExitDialogContent(elapsedTime).title}
        exitDescription={getExitDialogContent(elapsedTime).description}
      />

      <LpRecordingToast
        message={toastMessage}
        show={showToastInternal}
        onClose={() => {
          setShowToastInternal(false);
          onCloseToast?.();
        }}
      />
      <AlertDialog
        title="No Internet Connection"
        description="Seems like your internet is not connected. Connect and try again."
        showPopup={showNoInternetPopup}
        onClosePopup={() => {
          setShowNoInternetPopup(false);
        }}
      />

      {!showNoInternetPopup && (
        <>
          <StickyTopContainer>
            <SwitcherContainer isScrolling={isScrolling}>
              <TbLpStepsSwitcher
                stepName={currentSection?.sectionType ?? ''}
                stepTime={0}
                isLeftIconActive={currentSectionIndex > 0}
                isRightIconActive={
                  currentSectionIndex < sectionsWithContent.length - 1
                }
                isScrolling={isScrolling}
                onChange={handleChangeSection}
              />
            </SwitcherContainer>
            <Typography variant="textHeadlineSmallPrimary">
              {fluidLpDetail.title ?? ''}
            </Typography>
          </StickyTopContainer>

          <Spacing padding="xl" marginBottom="xl">
            {currentSection && renderVariants(currentSection.variants)}
          </Spacing>

          <ButtonContainer
            currentSectionIndex={currentSectionIndex}
            isLpCompleted={isLastSection}
          >
            {currentSectionIndex > 0 && !isLastSection && (
              <Button
                variant="outlined"
                onClick={() => handleChangeSection(Direction.Prev)}
                width="fit-content"
              >
                Back
              </Button>
            )}
            <Button
              variant="filled"
              onClick={handleButtonClick}
              width="fit-content"
            >
              {isLastSection
                ? recordingState !== 'idle'
                  ? 'Complete Lesson & Submit Recording'
                  : 'Lesson Complete'
                : 'Next'}
            </Button>
          </ButtonContainer>

          {isLastSection &&
            isRecordingEnabled &&
            recordingState === 'idle' &&
            onRecordStart && (
              <Spacing padding="none none xl none">
                <Typography variant="textTitleMedium" textAlign="center">
                  Want to record this lesson?
                </Typography>
                <Spacing marginTop="md" />
                <LpRecordingInviteCard onRecordStart={onRecordStart} />
              </Spacing>
            )}

          {isRecordingEnabled &&
            recordingState === 'idle' &&
            !isLastSection &&
            onRecordStart && (
              <LpRecordingIdleStickyFooter
                position="bottom"
                onRecordStart={onRecordStart}
              />
            )}

          {isRecordingEnabled &&
            recordingState !== 'idle' &&
            onRestart &&
            onPause &&
            onResume && (
              <LpRecordingStickyFooter
                position="bottom"
                elapsedLabel={elapsedLabel}
                isPaused={recordingState === 'paused'}
                onRestart={() => setShowRestartConfirmation(true)}
                onPause={onPause}
                onResume={onResume}
              />
            )}
        </>
      )}
    </StyledDiv>
  );
};

const ButtonContainer = styled.div<ButtonContainerProps>`
  display: flex;
  justify-content: ${(props) =>
    props.currentSectionIndex > 0 && !props.isLpCompleted
      ? 'space-between'
      : 'flex-end'};
  margin-top: var(--rk-spacing-xxxl);
  margin-bottom: var(--rk-spacing-xxxl);

  md-filled-button {
    ${mq(BreakpointSizes.xs)} {
      width: ${({ isLpCompleted }) => (isLpCompleted ? '100%' : '')};
    }
  }
`;

const SwitcherContainer = styled.div<SwitcherContainerProps>`
  padding-bottom: var(--rk-spacing-xxl);
  padding-top: ${({ isScrolling }) =>
    isScrolling ? 'var(--rk-spacing-lg)' : ''};
`;

const StickyTopContainer = styled.div`
  position: sticky;
  top: 0;
  padding-bottom: var(--rk-spacing-xxl);
  background-color: var(--md-sys-color-on-primary);
  z-index: 1;
`;
